import React from 'react';
import { Helmet } from 'react-helmet';
import { servicesDetailedData } from '../servicesData';
import {
  Headphones, Settings, Camera, Wifi, Code, Cloud, HardDrive, Server, CheckCircle, ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const iconMap = {
  Headphones, Settings, Camera, Wifi, Code, Cloud, HardDrive, Server
};

const ServicesPage = () => {
  return (
    <>
      <Helmet>
        <title>IT Services - R-Technos | Expert IT Consulting, Support & Solutions in Hyderabad</title>
        <meta name="description" content="Comprehensive IT services including consulting, support, AMC, CCTV security, networking, web development, cloud hosting, and server management. 24/7 support in Hyderabad." />
        <meta name="keywords" content="IT services Hyderabad, IT consulting, AMC services, CCTV installation, network setup, web development, cloud hosting, server management, IT support" />
        <link rel="canonical" href="https://rtechnos.com/services" />
        <meta property="og:title" content="IT Services - R-Technos | Expert IT Solutions" />
        <meta property="og:description" content="Professional IT services for businesses. 24/7 support, consulting, security, networking, and cloud solutions." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-white pt-20" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Text, sans-serif' }}>
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-white via-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-blue-600 text-sm font-semibold tracking-wider uppercase mb-3">
              Our Services
            </p>
            <h1 className="text-5xl md:text-6xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Expert IT Services
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 text-transparent bg-clip-text">
                for Your Business
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              From consulting and support to cloud hosting and security solutions, we deliver comprehensive IT services tailored to your business needs.
            </p>
            <Link to="/contact">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-base">
                Get Started Today
              </Button>
            </Link>
          </div>
        </section>

        {/* Services Detailed Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-24">
              {servicesDetailedData.map((service, index) => {
                const Icon = iconMap[service.icon];
                const isEven = index % 2 === 0;

                return (
                  <div key={service.id} className="relative">
                    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                      isEven ? '' : 'lg:flex-row-reverse'
                    }`}>
                      {/* Image */}
                      <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                        <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-80 object-cover"
                            loading="lazy"
                          />
                          <div className="absolute top-6 left-6">
                            <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
                              <Icon className="text-white" size={32} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                        <div className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">
                          Service #{service.id}
                        </div>
                        <h2 className="text-4xl font-semibold text-gray-900 mb-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
                          {service.title}
                        </h2>
                        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                          {service.fullDescription}
                        </p>

                        {/* Features */}
                        <div className="mb-6">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif' }}>
                            Key Features
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {service.features.map((feature, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Benefits */}
                        <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif' }}>
                            Benefits
                          </h3>
                          <ul className="space-y-2">
                            {service.benefits.map((benefit, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <ArrowRight className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                                <span className="text-gray-600 text-sm">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <Link to="/contact">
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6">
                            Request This Service
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, SF Pro Display, sans-serif', letterSpacing: '-0.02em' }}>
              Ready to Transform Your IT Infrastructure?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Contact us today for a free consultation and discover how our expert IT services can help your business thrive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base rounded-full shadow-md hover:shadow-lg transition-all">
                  Get Free Consultation
                </Button>
              </Link>
              <a href="tel:+917989109423">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-600 text-blue-600 hover:bg-white px-8 py-6 text-base rounded-full transition-all"
                >
                  Call: +91 79891 09423
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;